import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  UploadCloud,
  FileVideo,
  Pencil,
  CalendarClock,
  Loader2,
  CheckCircle2,
  XCircle,
  LayoutTemplate,
} from 'lucide-react';
import { toast } from 'sonner';
import { uploadFileWithProgress } from '@/hooks/useChunkedUpload';
import { getLinkedFolders } from '@/lib/templateFolders';
import { ProductionToolsDropdown } from '@/components/production/ProductionToolsDropdown';

const API_URL = window.__BACKEND_URL;

export default function ProjectUpload() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [loadingProject, setLoadingProject] = useState(true);
  const [uploads, setUploads] = useState([]); // [{id, name, status, progress, assetId, error}]
  const [dragActive, setDragActive] = useState(false);
  const [linkedFolders, setLinkedFolders] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const res = await fetch(`${API_URL}/api/projects/${projectId}`, { credentials: 'include' });
        if (!res.ok) throw new Error('Could not load this project');
        const data = await res.json();
        setProject(data.project);
      } catch (err) {
        toast.error(err.message || 'Could not load this project');
      } finally {
        setLoadingProject(false);
      }
    };
    fetchProject();
  }, [projectId]);

  useEffect(() => {
    if (project?.creator_id) setLinkedFolders(getLinkedFolders(project.creator_id, projectId));
  }, [project, projectId]);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(
      (f) => f.type.startsWith('video/') || f.type.startsWith('image/')
    );
    if (!files.length) return;
    if (!project?.creator_id) {
      toast.error('Project creator is missing — cannot upload');
      return;
    }

    const rows = files.map((f) => ({
      id: `${f.name}-${f.size}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      name: f.name,
      isImage: f.type.startsWith('image/'),
      status: 'uploading',
      progress: 0,
      assetId: null,
      error: null,
    }));
    setUploads((prev) => [...rows, ...prev]);

    const results = await Promise.allSettled(
      files.map((file, i) =>
        uploadFileWithProgress(file, {
          creatorId: project.creator_id,
          projectId,
          onProgress: (pct) => {
            setUploads((prev) => prev.map((u) => (u.id === rows[i].id ? { ...u, progress: pct } : u)));
          },
        }).then((created) => {
          setUploads((prev) => prev.map((u) => (u.id === rows[i].id ? { ...u, status: 'done', progress: 100, assetId: created.id } : u)));
          return created;
        }).catch((err) => {
          setUploads((prev) => prev.map((u) => (u.id === rows[i].id ? { ...u, status: 'failed', error: err.message } : u)));
          throw err;
        })
      )
    );

    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (succeeded > 0) toast.success(`${succeeded} file${succeeded > 1 ? 's' : ''} uploaded`);
    if (failed > 0) toast.error(`${failed} file${failed > 1 ? 's' : ''} failed to upload`);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const hasCompleted = uploads.some((u) => u.status === 'done');
  const completedAssetIds = uploads.filter((u) => u.status === 'done').map((u) => u.assetId);
  const scheduleHref = `/app/create/optimize?assetId=${completedAssetIds[0] || ''}&projectId=${projectId}&creatorId=${project?.creator_id || ''}`;

  if (loadingProject) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="project-upload-loading">
        <Loader2 className="h-6 w-6 text-[#8A93A6] animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container mx-auto p-6 max-w-5xl text-center py-24" data-testid="project-upload-not-found">
        <p className="text-[#8A93A6] mb-4">This project could not be found or you don't have access to it.</p>
        <Button variant="outline" className="border-white/10" onClick={() => navigate('/app/create/workspace')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Workspace
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-5xl">
      <div className="mb-4" data-testid="project-upload-breadcrumb">
        <p className="text-xs text-[#8A93A6] tracking-wide">
          <Link to="/app/create" className="hover:text-white transition-colors">Create</Link>
          <span className="mx-1.5 opacity-50">/</span>
          <Link to="/app/create/workspace" className="hover:text-white transition-colors">Workspace</Link>
          <span className="mx-1.5 opacity-50">/</span>
          <Link to={`/app/create/workspace/project/${projectId}`} className="hover:text-white transition-colors">{project.name}</Link>
          <span className="mx-1.5 opacity-50">/</span>
          <span className="text-[#C0C7D4]">Upload</span>
        </p>
      </div>

      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-white" data-testid="project-upload-title">Upload media to "{project.name}"</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <ProductionToolsDropdown currentTool="upload" creatorId={project?.creator_id} projectId={projectId} returnTo={`/app/create/workspace/project/${projectId}/upload`} />
          <Button
            variant="outline"
            className="border-white/10"
            disabled={!hasCompleted}
            onClick={() => navigate(scheduleHref)}
            title={hasCompleted ? undefined : 'Finish at least one upload first'}
            data-testid="project-upload-schedule-btn"
          >
            <CalendarClock className="h-4 w-4 mr-2" />
            Optimize &amp; Schedule
          </Button>
        </div>
      </div>

      {linkedFolders.length > 0 && (
        <div className="mb-6 flex items-center gap-2 text-xs text-[#8A93A6] bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2" data-testid="project-upload-linked-folders-banner">
          <LayoutTemplate className="h-3.5 w-3.5 text-[#3A7BFF] shrink-0" />
          <span>This project has {linkedFolders.length} linked Template Folder{linkedFolders.length > 1 ? 's' : ''} ({linkedFolders.map((f) => f.name).join(', ')}) — once you optimize this upload, the Editor, Clips, Optimize and Schedule tools will offer their matching templates automatically.</span>
          <Link to={`/app/create/templates?projectId=${projectId}&creatorId=${project?.creator_id || ''}`} className="text-[#3A7BFF] hover:text-white shrink-0 ml-auto" data-testid="project-upload-manage-templates-link">Manage</Link>
        </div>
      )}

      {/* Drop zone */}
      <div
        className={`rounded-xl border-2 border-dashed p-10 text-center mb-8 transition-colors ${dragActive ? 'border-blue-400 bg-blue-400/5' : 'border-white/10'}`}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => { e.preventDefault(); setDragActive(false); handleFiles(e.dataTransfer.files); }}
        data-testid="project-upload-dropzone"
      >
        <UploadCloud className="h-8 w-8 text-[#8A93A6] mx-auto mb-3" />
        <p className="text-white font-medium mb-1">Drag and drop videos or images</p>
        <p className="text-[#8A93A6] text-sm mb-4">or</p>
        <Button onClick={() => fileInputRef.current?.click()} data-testid="project-upload-browse-btn">
          Browse files
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="video/*,image/*"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="project-upload-file-input"
        />
      </div>

      {/* Upload list */}
      {uploads.length > 0 && (
        <div className="space-y-3" data-testid="project-upload-list">
          {uploads.map((u) => (
            <div key={u.id} className="glass border border-white/5 rounded-lg p-4 flex items-center gap-4" data-testid={`project-upload-row-${u.id}`}>
              <FileVideo className="h-5 w-5 text-[#8A93A6] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm truncate">{u.name}</p>
                {u.status === 'uploading' && <Progress value={u.progress} className="h-1.5 mt-2" />}
                {u.status === 'failed' && <p className="text-red-400 text-xs mt-1">{u.error || 'Upload failed'}</p>}
              </div>
              {u.status === 'uploading' && <Badge variant="outline" className="border-white/10 text-[#8A93A6]">{u.progress}%</Badge>}
              {u.status === 'done' && (
                <>
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                  {u.isImage ? (
                    <Badge variant="outline" className="border-white/10 text-[#8A93A6]" title="The Editor only supports video right now">
                      Image — no editor
                    </Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-white/10 shrink-0"
                      onClick={() => navigate(`/app/create/editor/${u.assetId}`)}
                      data-testid={`project-upload-edit-${u.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1.5" />
                      Edit
                    </Button>
                  )}
                </>
              )}
              {u.status === 'failed' && <XCircle className="h-4 w-4 text-red-400 shrink-0" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
